/**
 * Shopify Distributor Finder Locator Orchestration Script
 * Eisco Labs | Website Project
 * Last updated: May 2026
 *
 * Handles client-side form submissions, AJAX API requests (with HMAC signatures),
 * state rendering (loading skeletons, feedback alerts), and interactive Google Maps.
 */

(function () {
  'use strict';

  // Locate the DOM root container. If not present on the current page template, abort.
  const root = document.getElementById('distributor-finder');
  if (!root) return;

  // ── 1. Read Settings & Credentials from Liquid Mounting Attributes ──
  const API_BASE = root.dataset.apiBase;
  const SIGNATURE = root.dataset.signature;
  const TIMESTAMP = root.dataset.timestamp;
  const SKU = root.dataset.sku || undefined;
  const CUSTOMER_ID = root.dataset.customerId || undefined;
  // 'true' only when a customer is logged in. Bound into the HMAC signature, so
  // it must be sent verbatim with every request or the signature won't match.
  const LOGGED_IN = root.dataset.loggedIn === 'true' ? 'true' : 'false';
  const MAPS_KEY = root.dataset.mapsKey;
  const LOCKED_COUNTRY = root.dataset.lockedCountry || 'US';
  const CUSTOMER_ZIP = root.dataset.customerZip || undefined;

  // ── 2. Configuration & State Variables ──
  const LIMIT = 10;
  let currentCountry = LOCKED_COUNTRY;
  let currentZip = '';
  let currentMaxDistance = '';
  let currentOffset = 0;
  let totalCount = 0;
  let searchInProgress = false;
  let isLocationResetInSession = false;

  // Google Maps Instance Store
  let googleMap = null;
  let mapMarkers = [];
  let googleMapsLoaded = false;

  // ── 3. DOM Element Caching ──
  const form = document.getElementById('distributor-form');
  const countrySelect = document.getElementById('df-country-select');
  const zipInput = document.getElementById('df-zip-input');
  const radiusSelect = document.getElementById('df-radius-select');
  
  const resultsPanel = document.getElementById('distributor-results');
  const listContainer = document.getElementById('distributor-list');
  const loadMoreBtn = document.getElementById('load-more');
  const noResultsEl = document.getElementById('no-results');
  const errorEl = document.getElementById('df-error-alert');
  const errorMessageEl = document.getElementById('df-error-message');
  
  const welcomeView = document.getElementById('df-welcome-view');
  const skeletonLoader = document.getElementById('df-skeleton-loader');
  const countBadge = document.getElementById('df-count-badge');
  const clearBtn = document.getElementById('df-clear-search');
  const submitBtn = form.querySelector('button[type="submit"]');
  const submitBtnText = submitBtn.querySelector('.df-btn-text');
  const submitBtnLoader = submitBtn.querySelector('.df-btn-loader');

  // Locked Status UI Elements (Layer 3 Gating)
  const lockedCard = document.getElementById('df-locked-location-card');
  const lockedCountryName = document.getElementById('df-locked-country-name');
  const lockedZipBadge = document.getElementById('df-locked-zip-badge');
  const resetLocationBtn = document.getElementById('df-reset-location');

  const countryNames = {
    US: 'United States',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    IN: 'India'
  };

  // Lock country select element to geolocated region
  if (countrySelect) {
    countrySelect.value = LOCKED_COUNTRY;
    countrySelect.disabled = true;
  }

  // ── 3.5 Gating Location Persistence & Swap Helpers ──
  function getSavedLocation() {
    try {
      // 1. Check local storage first
      const stored = localStorage.getItem('df_locked_location');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.country === LOCKED_COUNTRY) {
          return parsed;
        }
      }

      // 2. If no local storage saved location, auto-load logged-in customer's default ZIP (unless explicitly reset in session)
      if (!isLocationResetInSession && CUSTOMER_ZIP) {
        return {
          country: LOCKED_COUNTRY,
          zip: CUSTOMER_ZIP
        };
      }
    } catch (e) {
      console.warn('Failed to parse saved location', e);
    }
    return null;
  }

  function updateLockedLocationUI(zip) {
    if (!lockedCard) return;

    const countryName = countryNames[LOCKED_COUNTRY] || LOCKED_COUNTRY;
    if (lockedCountryName) lockedCountryName.textContent = countryName;
    
    if (lockedZipBadge) {
      if (zip) {
        lockedZipBadge.textContent = `(${zip})`;
        lockedZipBadge.style.display = 'inline-block';
      } else {
        lockedZipBadge.style.display = 'none';
      }
    }

    // Contextual lock descriptions for traveling or VPN users
    const lockedDesc = lockedCard.querySelector('.df-locked-desc');
    if (lockedDesc) {
      if (CUSTOMER_ID) {
        lockedDesc.textContent = `Your search is locked to your verified account country (${countryName}). If you are traveling or using a VPN, please log out of your account to lookup local distributors in other regions.`;
      } else {
        lockedDesc.textContent = `Your search is secured and locked to this country (${countryName}). If you are using a VPN or proxy that mismatches your current physical country, please disable it to search local partners.`;
      }
    }

    form.style.display = 'none';
    lockedCard.hidden = false;
  }

  function resetLockedLocationUI() {
    isLocationResetInSession = true;
    try {
      localStorage.removeItem('df_locked_location');
    } catch (e) {}

    currentZip = '';
    zipInput.value = '';
    
    if (lockedCard) lockedCard.hidden = true;
    form.style.display = 'block';

    // Clear list and markers
    listContainer.innerHTML = '';
    clearMarkers();
    
    // Reset view visibility
    resultsPanel.hidden = true;
    welcomeView.hidden = false;
    noResultsEl.hidden = true;
    errorEl.hidden = true;
    
    if (googleMap) {
      googleMap.setZoom(2);
      googleMap.setCenter({ lat: 20, lng: 0 });
    }
  }

  if (resetLocationBtn) {
    resetLocationBtn.addEventListener('click', resetLockedLocationUI);
  }

  // ── 4. Setup Event Listeners ──
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (searchInProgress) return;

    currentCountry = LOCKED_COUNTRY;
    currentZip = zipInput.value.trim();
    currentMaxDistance = radiusSelect ? radiusSelect.value : '';
    currentOffset = 0;

    // Reset UI Panel layouts
    listContainer.innerHTML = '';
    welcomeView.hidden = true;
    noResultsEl.hidden = true;
    errorEl.hidden = true;
    loadMoreBtn.hidden = true;
    resultsPanel.hidden = false;

    await executeSearch(true);
  });

  loadMoreBtn.addEventListener('click', async () => {
    if (searchInProgress) return;
    await executeSearch(false);
  });

  clearBtn.addEventListener('click', () => {
    resetLockedLocationUI();
  });

  // ── 5. Main Search Fetch Engine ──
  async function executeSearch(isNewSearch = true) {
    toggleLoadingState(true, isNewSearch);
    searchInProgress = true;

    try {
      // Build API query string
      const params = new URLSearchParams({
        country: currentCountry,
        logged_in: LOGGED_IN,
        limit: LIMIT,
        offset: currentOffset,
      });

      if (currentZip) params.set('zip', currentZip);
      if (currentMaxDistance) params.set('max_distance_km', currentMaxDistance);
      if (SKU) params.set('sku', SKU);
      if (CUSTOMER_ID) params.set('customer_id', CUSTOMER_ID);

      const url = `${API_BASE}/distributors/search?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Request-Signature': SIGNATURE,
          'X-Request-Timestamp': TIMESTAMP,
        },
      });

      if (!response.ok) {
        let errData;
        try {
          errData = await response.json();
        } catch {
          errData = {};
        }

        // The API returns { error, details? }. `details` is a string for the
        // ZIP-quota 429 and a field->messages map for 400 validation errors.
        // Surface the server's own wording rather than inventing our own.
        let serverMessage = errData.error || '';
        if (errData.details) {
          if (typeof errData.details === 'string') {
            serverMessage = serverMessage
              ? `${serverMessage} ${errData.details}`
              : errData.details;
          } else if (typeof errData.details === 'object') {
            const fieldMessages = Object.values(errData.details).flat().filter(Boolean);
            if (fieldMessages.length) {
              serverMessage = serverMessage
                ? `${serverMessage}: ${fieldMessages.join('; ')}`
                : fieldMessages.join('; ');
            }
          }
        }

        let friendlyError = serverMessage;

        if (response.status === 401) {
          // 401s here are signature problems, not location problems. The
          // country and login state are locked and signed at page render, so
          // a visitor can't cause a country/VPN mismatch. The common cause is
          // a stale page: the signature is valid for only 5 minutes.
          friendlyError =
            'This page has been open too long and its secure token expired. Please reload the page and search again. If the problem persists, contact customer support.';
        } else if (response.status === 422) {
          // Geocoding failed for the given zip in the locked country.
          friendlyError =
            serverMessage ||
            "We couldn't locate that postal code. Please double-check it for your region and try again.";
        } else if (response.status === 429) {
          // Two distinct 429s exist: the 60-req/min IP limit and the
          // 3-distinct-zips-per-24h scraping guard. Both ship a useful message,
          // so prefer the server's text instead of guessing which one fired.
          friendlyError =
            serverMessage ||
            'Too many searches in a short time. Please wait a moment and try again.';
        }

        throw new Error(friendlyError || `Request failed (HTTP ${response.status})`);
      }

      const json = await response.json();
      const distributors = json.data || [];
      totalCount = json.meta?.total_count || 0;
      currentOffset += distributors.length;

      // Persist verified location and lock storefront UI bounds
      if (isNewSearch) {
        try {
          localStorage.setItem('df_locked_location', JSON.stringify({
            country: currentCountry,
            zip: currentZip
          }));
        } catch (e) {}
        updateLockedLocationUI(currentZip);
      }

      // Update counters
      countBadge.textContent = `${totalCount} ${totalCount === 1 ? 'Match' : 'Matches'}`;

      if (isNewSearch) {
        clearMarkers();
        // Ensure Google Maps is loaded asynchronously before plotting
        if (MAPS_KEY && !googleMapsLoaded) {
          await loadGoogleMapsScript();
        }
      }

      if (isNewSearch && distributors.length === 0) {
        noResultsEl.hidden = false;
        loadMoreBtn.hidden = true;
        toggleLoadingState(false, isNewSearch);
        searchInProgress = false;
        return;
      }

      // Render cards to list
      distributors.forEach((d) => {
        listContainer.appendChild(renderCard(d));
      });

      // Render pins on Map
      if (googleMapsLoaded && googleMap) {
        plotDistributorMarkers(distributors, isNewSearch);
      }

      // Handle Pagination visibility
      loadMoreBtn.hidden = currentOffset >= totalCount;

    } catch (err) {
      console.error('Distributor Locator error:', err);
      errorMessageEl.textContent = err.message || 'An unexpected error occurred. Please try again.';
      errorEl.hidden = false;
      loadMoreBtn.hidden = true;
      if (isNewSearch) {
        listContainer.innerHTML = '';
      }
    } finally {
      toggleLoadingState(false, isNewSearch);
      searchInProgress = false;
    }
  }

  // ── 6. UI DOM Render Helpers ──
  function toggleLoadingState(isLoading, isNewSearch) {
    if (isLoading) {
      submitBtn.disabled = true;
      if (isNewSearch) {
        skeletonLoader.hidden = false;
      } else {
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'Loading...';
      }
      submitBtnText.style.opacity = '0.4';
      submitBtnLoader.hidden = false;
    } else {
      submitBtn.disabled = false;
      skeletonLoader.hidden = true;
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = 'Load More Distributors';
      submitBtnText.style.opacity = '1';
      submitBtnLoader.hidden = true;
    }
  }

  function renderCard(d) {
    const card = document.createElement('div');
    card.className = 'df-card';
    card.setAttribute('role', 'article');
    card.setAttribute('tabindex', '0');

    // Attach map focus trigger on card click (if coords exist)
    if (d.lat !== null && d.lng !== null) {
      card.addEventListener('click', () => {
        focusMapOnCoordinate(d.lat, d.lng, d.name);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          focusMapOnCoordinate(d.lat, d.lng, d.name);
        }
      });
    }

    const titleEl = document.createElement('h3');
    titleEl.className = 'df-card-title';
    titleEl.textContent = d.name;

    card.appendChild(titleEl);

    // Render stocks badge
    if (d.has_bought_sku) {
      const badge = document.createElement('span');
      badge.className = 'df-badge df-card-badge';
      badge.textContent = 'Stocks Product';
      card.appendChild(badge);
    }

    // Proximity / distance info
    if (d.distance_km !== null) {
      const meta = document.createElement('div');
      meta.className = 'df-card-meta';
      meta.innerHTML = `
        <svg class="df-contact-icon" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
        </svg>
        <span>${d.distance_km.toFixed(1)} km away</span>
      `;
      card.appendChild(meta);
    }

    // Street address
    if (d.address) {
      const addr = document.createElement('p');
      addr.className = 'df-card-address';
      addr.textContent = d.address;
      card.appendChild(addr);
    }

    // Contacts section
    if (d.sales_contact_email || d.sales_contact_phone || d.website_url) {
      const contacts = document.createElement('div');
      contacts.className = 'df-card-contacts';

      if (d.sales_contact_email) {
        contacts.innerHTML += `
          <a class="df-contact-link" href="mailto:${d.sales_contact_email}">
            <svg class="df-contact-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <span>${d.sales_contact_email}</span>
          </a>
        `;
      }

      if (d.sales_contact_phone) {
        contacts.innerHTML += `
          <a class="df-contact-link" href="tel:${d.sales_contact_phone}">
            <svg class="df-contact-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.84c0-1.222.988-2.21 2.21-2.21h2.234c1.222 0 2.21.988 2.21 2.21v1.157c0 1.222-.988 2.21-2.21 2.21H5.45c-.328 0-.649.073-.943.21l-1.077.502a2 2 0 00-1.18 1.83v2.247A2.25 2.25 0 004.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25z" />
            </svg>
            <span>${d.sales_contact_phone}</span>
          </a>
        `;
      }

      if (d.website_url) {
        contacts.innerHTML += `
          <a class="df-contact-link" href="${d.website_url}" target="_blank" rel="noopener">
            <svg class="df-contact-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-.778.099-1.533.284-2.253" />
            </svg>
            <span>Visit Website</span>
          </a>
        `;
      }

      card.appendChild(contacts);
    }

    return card;
  }

  // ── 7. Google Maps Integration Functions ──
  function loadGoogleMapsScript() {
    return new Promise((resolve, reject) => {
      if (window.google && window.google.maps) {
        googleMapsLoaded = true;
        initializeMapInstance();
        resolve();
        return;
      }

      // Bind global callback
      window.initDistributorFinderMap = () => {
        googleMapsLoaded = true;
        initializeMapInstance();
        resolve();
      };

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&callback=initDistributorFinderMap`;
      script.async = true;
      script.defer = true;
      script.onerror = (err) => {
        console.error('Google Maps script failed to load:', err);
        reject(new Error('Failed to load map services'));
      };
      
      document.head.appendChild(script);
    });
  }

  function initializeMapInstance() {
    const canvas = document.getElementById('distributor-map');
    if (!canvas) return;

    // Premium styling config for Eisco locator map (simplified slate styling)
    const mapStyleOptions = [
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e9e9e9' }, { lightness: 17 }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }, { lightness: 20 }] },
      { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }, { lightness: 17 }] },
      { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }] },
      { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 18 }] },
      { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 16 }] },
      { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }, { lightness: 21 }] },
      { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dedede' }, { lightness: 21 }] }
    ];

    googleMap = new window.google.maps.Map(canvas, {
      zoom: 2,
      center: { lat: 20, lng: 0 },
      styles: mapStyleOptions,
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
      },
    });
  }

  function plotDistributorMarkers(distributors, isNewSearch) {
    if (!googleMap) return;

    const bounds = isNewSearch
      ? new window.google.maps.LatLngBounds()
      : getExistingMarkersBounds();

    distributors.forEach((d) => {
      if (d.lat === null || d.lng === null) return;

      const position = { lat: parseFloat(d.lat), lng: parseFloat(d.lng) };

      // Pin colors: Primary brand color for standard; Accent color for stock badge matches
      const pinColor = d.has_bought_sku ? '#00a8cc' : '#0b3c5d';

      const marker = new window.google.maps.Marker({
        position,
        map: googleMap,
        title: d.name,
        icon: {
          path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: pinColor,
          fillOpacity: 0.9,
          strokeWeight: 1.5,
          strokeColor: '#ffffff',
        },
      });

      // Build stylized InfoWindow bubble markup
      const infoWindowContent = `
        <div class="df-info-window">
          <h4>${d.name}</h4>
          ${d.address ? `<p>${d.address}</p>` : ''}
          <div class="df-info-window-links">
            ${d.sales_contact_email ? `<a href="mailto:${d.sales_contact_email}">Email Partner</a>` : ''}
            ${d.sales_contact_phone ? `<a href="tel:${d.sales_contact_phone}">Call Partner</a>` : ''}
            ${d.website_url ? `<a href="${d.website_url}" target="_blank" rel="noopener">Visit Website</a>` : ''}
            <a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.address || d.name)}" target="_blank" rel="noopener">Get Directions →</a>
          </div>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent,
      });

      marker.addListener('click', () => {
        // Close other active InfoWindows first
        mapMarkers.forEach((m) => m.infoWindow?.close());
        infoWindow.open(googleMap, marker);
      });

      // Store references
      marker.infoWindow = infoWindow;
      mapMarkers.push(marker);
      bounds.extend(position);
    });

    // Auto-fit zoom and frame coordinates dynamically
    if (mapMarkers.length > 0) {
      googleMap.fitBounds(bounds);

      // Prevent zooming in too close for a single result
      const listener = window.google.maps.event.addListener(googleMap, 'idle', () => {
        if (googleMap.getZoom() > 14) {
          googleMap.setZoom(14);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }

  function focusMapOnCoordinate(lat, lng, name) {
    if (!googleMap) return;

    const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
    googleMap.panTo(position);
    googleMap.setZoom(12);

    // Find and trigger the corresponding marker's InfoWindow
    const matchingMarker = mapMarkers.find(
      (m) =>
        Math.abs(m.getPosition().lat() - position.lat) < 0.0001 &&
        Math.abs(m.getPosition().lng() - position.lng) < 0.0001
    );

    if (matchingMarker) {
      mapMarkers.forEach((m) => m.infoWindow?.close());
      matchingMarker.infoWindow.open(googleMap, matchingMarker);
    }
  }

  function getExistingMarkersBounds() {
    const bounds = new window.google.maps.LatLngBounds();
    mapMarkers.forEach((m) => {
      bounds.extend(m.getPosition());
    });
    return bounds;
  }

  function clearMarkers() {
    mapMarkers.forEach((m) => {
      m.infoWindow?.close();
      m.setMap(null);
    });
    mapMarkers = [];
  }

  // ── 8. Boot & Auto-Run Initialization ──
  const savedLoc = getSavedLocation();
  if (savedLoc) {
    currentCountry = savedLoc.country;
    currentZip = savedLoc.zip;
    currentMaxDistance = '';
    currentOffset = 0;

    if (zipInput) zipInput.value = savedLoc.zip;
    updateLockedLocationUI(savedLoc.zip);

    listContainer.innerHTML = '';
    welcomeView.hidden = true;
    noResultsEl.hidden = true;
    errorEl.hidden = true;
    loadMoreBtn.hidden = true;
    resultsPanel.hidden = false;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => executeSearch(true));
    } else {
      executeSearch(true);
    }
  }
})();